import React from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { Mutation } from '@apollo/client/react/components';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';

import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import StaffPickBadge from 'four-nets/ui-v2/badges/staff-pick-badge/staff-pick-badge';
import photo from 'four-nets/icons/svg/icon_photo-outlined copy.svg';
import activity from 'four-nets/icons/svg/icon_activity.svg.bak.svg';
import Badge from 'four-nets/ui-v2/badges/badge/badge';
import SelectionItems from 'four-nets/photoblog/posts/common/selection-items/selection-items';
import Avatar from 'four-nets/avatar/avatar';
import { displayedName } from 'four-nets/user/utils';

import updatePost, { UpdatePostMutation, UpdatePostMutationVariables } from './mutation/mutationPickStaff.graphql';
import addToStaffPickSuggestions, {
    AddToStaffPickSuggestionsMutation,
    AddToStaffPickSuggestionsMutationVariables,
} from './mutation/mutationSuggestStaff.graphql';

import { DescriptionAlbumFragment } from './description.graphql';
import { PostType } from './description.graphql.types';

import defaultStyles from './description.scss';

export interface Props extends DescriptionAlbumFragment {
    styles: typeof defaultStyles;
}

export interface State {
    showMore: boolean;
    isStaffpick: boolean;
    addedToSuggestedStaffPicks: boolean;
}

class Description extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            showMore: false,
            addedToSuggestedStaffPicks: false,
            isStaffpick: this.props.isStaffpick !== undefined && this.props.isStaffpick!,
        };
    }

    render() {
        const { styles, title, imagesCount, isNew, createdTime, postType, id, user, selectionItems, isWeddingAlbum } =
            this.props;

        return (
            <div className={styles.wrapper}>
                <h1 className={styles.title}>{title}</h1>
                <div className={styles.albumBadges}>
                    {postType === 'POST_ALBUM' ? (
                        <Badge src={photo}>{imagesCount}</Badge>
                    ) : user && user.profile ? (
                        <Badge>
                            <span style={{ display: 'flex' }}>
                                <Avatar style={{ avatar: styles.avatar }} user={user} />
                                <Link
                                    className={styles.usernameLink}
                                    to={
                                        user.profile.isVendor
                                            ? `/catalog/vendor/${user.username}/`
                                            : `/blog/${user.username}/`
                                    }
                                >
                                    {displayedName(user)}
                                </Link>
                            </span>
                        </Badge>
                    ) : null}
                    <Badge src={activity}>
                        <FormattedDateAgoView value={new Date(createdTime)} />
                    </Badge>
                    {isNew ? (
                        <div className={styles.new}>
                            <FormattedMessage id='NEW_ALBUM' />
                        </div>
                    ) : null}
                    <SelectionItems selectionItems={selectionItems} />
                    {this.state.isStaffpick ? <StaffPickBadge /> : null}
                    <ViewerConsumer>
                        {({ viewer }) =>
                            viewer ? (
                                <React.Fragment>
                                    {!this.state.isStaffpick ? (
                                        <Mutation<
                                            AddToStaffPickSuggestionsMutation,
                                            AddToStaffPickSuggestionsMutationVariables
                                        >
                                            mutation={addToStaffPickSuggestions}
                                        >
                                            {addToStaffPick => (
                                                <button
                                                    className={classnames(styles.staffSuggestion, {
                                                        [styles.staffSuggestionsDisabled]:
                                                            this.state.addedToSuggestedStaffPicks,
                                                    })}
                                                    disabled={this.state.addedToSuggestedStaffPicks}
                                                    onClick={() => {
                                                        addToStaffPick({
                                                            variables: {
                                                                input: {
                                                                    id,
                                                                },
                                                            },
                                                        });
                                                        this.setState({ addedToSuggestedStaffPicks: true });
                                                    }}
                                                >
                                                    {this.state.addedToSuggestedStaffPicks ? (
                                                        <FormattedMessage id='THANKS_FOR_STAFFPICK' />
                                                    ) : (
                                                        <FormattedMessage
                                                            id='SUGGEST_FOR_STAFFPICKS'
                                                            values={{ gender: 'f' }}
                                                        />
                                                    )}
                                                </button>
                                            )}
                                        </Mutation>
                                    ) : null}
                                    <Mutation<UpdatePostMutation, UpdatePostMutationVariables> mutation={updatePost}>
                                        {update =>
                                            viewer.isSuperstaff &&
                                            (postType === 'POST_ALBUM' || postType === 'POST_ARTICLE') ? (
                                                <React.Fragment>
                                                    <div className={styles.divider}>•</div>
                                                    <button
                                                        className={styles.addToStaffPick}
                                                        onClick={() => {
                                                            this.setState(prevState => {
                                                                update({
                                                                    variables: {
                                                                        input: {
                                                                            id,
                                                                            isStaffpick: !prevState.isStaffpick,
                                                                        },
                                                                    },
                                                                });
                                                                return { isStaffpick: !prevState.isStaffpick };
                                                            });
                                                        }}
                                                    >
                                                        {this.state.isStaffpick
                                                            ? 'Remove from staff pick'
                                                            : 'Add to staff pick'}
                                                    </button>
                                                </React.Fragment>
                                            ) : null
                                        }
                                    </Mutation>
                                </React.Fragment>
                            ) : null
                        }
                    </ViewerConsumer>
                </div>
            </div>
        );
    }
}

export default Styled(defaultStyles)(Description);
